import { Link } from "@inertiajs/react";
import { Share2, Copy, Heart, Mail, MessageCircle, Link2 } from "lucide-react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/shared/StatusBadge";
import ProgressBar from "@/components/shared/ProgressBar";

function PublicDonationPagePreview() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2 mb-1">
          <StatusBadge status="active" />
          <span className="text-xs text-muted-foreground">Spring Read-a-thon 2026</span>
        </div>
        <CardTitle className="text-2xl">Sarah T. is reading for Lincoln Elementary</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">Room 12 · Grade 5</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-primary/10 px-4 py-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-primary mb-1">Raised</p>
            <p className="font-display text-xl font-semibold text-primary">$75</p>
          </div>
          <div className="rounded-lg bg-accent/10 px-4 py-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-accent mb-1">Minutes read</p>
            <p className="font-display text-xl font-semibold text-accent">135</p>
          </div>
        </div>
        <ProgressBar label="School-wide" detail="$3,210 of $5,000" pct={64} />
        <Button size="lg" className="w-full"><Heart className="h-4 w-4" /> Donate to Sarah</Button>
      </CardContent>
    </Card>
  );
}

// Static mock of StudentShareDialog's contents. Embedding the real
// dialog would lock body scroll and demand modal interaction even
// inside the inert Screenshot wrapper, so this stays hand-built and
// just needs to track the dialog's visual layout.
function ShareDialogPreview() {
  return (
    <div className="rounded-xl border border-border bg-card text-card-foreground shadow-sm overflow-hidden">
      <div className="border-b border-border p-4">
        <h3 className="font-display text-lg font-semibold text-foreground">Share Sarah's page</h3>
        <p className="text-xs text-muted-foreground mt-1">Send it to family and friends who'd like to cheer them on.</p>
      </div>
      <div className="p-4 space-y-4">
        <Button className="w-full"><Share2 className="h-4 w-4" /> Share…</Button>
        <div className="grid grid-cols-2 gap-2">
          <ShareActionTile icon={<Mail className="h-4 w-4" />} label="Email" />
          <ShareActionTile icon={<MessageCircle className="h-4 w-4" />} label="Text message" />
        </div>
        <div className="flex items-stretch gap-2 rounded-lg border border-border bg-background p-2">
          <div className="flex-1 min-w-0 px-3 py-2 font-mono text-xs text-foreground truncate">
            pagepledge.com/p/dyq8s4-bear-moon-42
          </div>
          <Button size="sm" variant="outline"><Link2 className="h-4 w-4" /> Copy link</Button>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Or copy a message
          </p>
          <div className="rounded-lg border border-border bg-background p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                A note to family
              </span>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs">
                <Copy className="h-3 w-3" /> Copy
              </Button>
            </div>
            <p className="text-sm text-foreground leading-relaxed">
              Sarah is doing a read-a-thon to raise money for Lincoln Elementary. If you'd like to cheer them on, you can donate on their page: pagepledge.com/p/dyq8s4-bear-moon-42
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShareActionTile({ icon, label }) {
  return (
    <div className="inline-flex flex-col items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-2 py-3 text-xs font-medium text-foreground">
      {icon}
      <span>{label}</span>
    </div>
  );
}

export default function SharingWithFamily() {
  return (
    <>
      <p>
        The single biggest thing you can do for the read-a-thon is share your child's donation page. Grandparents, aunts, uncles, family group chats. Anyone you send the link to can donate without making an account.
      </p>

      <h2>What family will see</h2>
      <Screenshot caption="What the donation page looks like" browser="pagepledge.com/p/dyq8s4-bear-moon-42">
        <div className="max-w-md mx-auto">
          <PublicDonationPagePreview />
        </div>
      </Screenshot>
      <p>
        Your child's first name and last initial, classroom, school, how much they've raised, how many minutes they've read, recent donations, and what the school can earn together — everything a donor needs. No photos or personal info beyond their name.
      </p>

      <h2>How to share</h2>
      <p>
        On your child's page, tap <strong>Share donation page</strong>. A small window opens with everything in one place: your phone's share menu, buttons for email and text, the link to copy, and a few messages you can paste anywhere.
      </p>
      <Screenshot caption="The share window">
        <div className="max-w-md mx-auto">
          <ShareDialogPreview />
        </div>
      </Screenshot>
      <p>
        On a phone, <strong>Share…</strong> opens your device's share menu so you can send to WhatsApp, Messenger, a family group chat, or anywhere else you'd normally share a link. On a laptop, the email and text buttons open your email or messages app with one of the suggested messages filled in.
      </p>

      <Callout variant="tip" title="Pick a suggested message">
        <p>
          A note alongside the link gets roughly twice as many donations as a bare link. The share window already includes a few short messages centred on your child — pick the one that sounds most like you, copy it, and paste it wherever you're sharing.
        </p>
      </Callout>

      <h2>Who to send it to</h2>
      <p>
        Grandparents tend to be the most generous, but family group chats, aunts and uncles, godparents, friends, neighbours, and supportive coworkers all chip in well. We'd suggest sticking to people you actually know — public posts on Facebook or Instagram bring in less and open the link up to scams. <Link href="/help/articles/leaderboards-and-privacy">More on what's safer</Link>.
      </p>

      <Callout variant="tip" title="Share twice">
        <p>
          Most donations come in two waves: right after you first share, and in the last few days of the campaign. So share once when reading begins, then again with a "we're almost there!" message near the end. It doubles most schools' results.
        </p>
      </Callout>

      <p>
        The link doesn't change — you can save it, paste it in the family Christmas letter, anywhere. It's a long random web address, so only people you send it to can find it.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/leaderboards-and-privacy">Leaderboards and your child's privacy</Link>.</li>
        <li><Link href="/help/articles/making-a-donation">What donors actually do</Link>.</li>
      </ul>
    </>
  );
}
